import React, { useState } from 'react';
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Alert,
} from '@cloudscape-design/components';
import { ExportValidation, SPACE_FILTER_REGEX } from './utils/ExportValidation';
import { useFlashbar } from 'src/common/hooks/useFlashbar';

interface ExportModalProps {
  visible: boolean;
  onClose: () => void;
  onExport: (exportName: string) => Promise<void>;
  activeFilters: Record<string, string[]>;
  onNavigateToExports?: () => void;
  description?: string;
  title?: string;
}

export const ExportModal: React.FC<ExportModalProps> = ({
  visible,
  onClose,
  onExport,
  activeFilters,
  onNavigateToExports,
  description,
  title,
}) => {
  const [exportName, setExportName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { addFlashbarMessage } = useFlashbar();

  const handleExportNameChange = (value: string) => {
    const filteredValue = value.replace(SPACE_FILTER_REGEX, '');
    setExportName(filteredValue);
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleSubmit = async () => {
    const error = ExportValidation.validateExportName(exportName);
    if (error) {
      setValidationError(error);
      return;
    }

    setIsLoading(true);
    setValidationError(null);

    try {
      await onExport(exportName);
      addFlashbarMessage({
        type: 'success',
        header: 'Export request submitted successfully!',
        content: 'Your export is being processed.',
        action: onNavigateToExports ? (
          <Button onClick={onNavigateToExports}>Go to Exports</Button>
        ) : undefined,
        dismissible: true,
        id: 'export_success',
      });
      onClose();
      setExportName('');
    } catch (error: any) {
      console.error('Export failed:', error);
      addFlashbarMessage({
        type: 'error',
        header: 'Export Failed',
        content: 'Failed to create export. Please try again.',
        dismissible: true,
        id: 'export_error',
      });
      onClose();
      setExportName('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setExportName('');
    setValidationError(null);
    onClose();
  };

  return (
    <Modal
      onDismiss={handleClose}
      visible={visible}
      closeAriaLabel="Close modal"
      size="medium"
      header={title || 'Export GL Data'}
    >
      <SpaceBetween size="m">
        <Box>
          <p>
            {description ||
              'This will export the data with your current applied filters. The export will be available in the Exports section once processing is complete.'}
          </p>
        </Box>

        <Alert type="info">
          <strong>Export Name Guidelines:</strong>
          <ul style={{ marginTop: '8px', marginBottom: '0' }}>
            <li>Use only letters, numbers, underscores (_), hyphens (-), and periods (.)</li>
            <li>Must be between 1 and 100 characters</li>
            <li>Each export name must be unique for your account</li>
          </ul>
        </Alert>

        <FormField
          label="Export Name"
          errorText={validationError}
          description="Enter a unique name for your export"
        >
          <Input
            value={exportName}
            onChange={({ detail }) => handleExportNameChange(detail.value)}
            placeholder="e.g., monthly_report_2024"
            disabled={isLoading}
          />
        </FormField>

        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={isLoading}
              disabled={isLoading}
            >
              Export Data
            </Button>
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </Modal>
  );
};
